// Navigator Evaluation Service - Risk prioritization for Navigator assessments
// Uses Navigator-specific Redux state (navigatorRisks, not explorerRisks or modelRisks)
import {
  updateNavigatorRiskPriority
} from '../../redux/slices/risksSlice';
import { authFetch } from '../../utils/authFetch';
import {
  setExplorerRiskEvaluation,
  saveExplorerEvaluation
} from '../../redux/slices/riskEvaluationsSlice';

export class NavigatorEvaluationService {
  constructor(dispatch, modelId, assessmentUuid, contextData = {}, impactCategories = [], liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.contextData = contextData;
    this.impactCategories = impactCategories;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeEvaluation(areaKey, areaDefinition, riskFormTaxonomy, inheritedRisks, progressCallback) {
    try {
      if (progressCallback) progressCallback('evaluation');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Access Navigator risks (not explorerRisks or modelRisks)
      const navigatorRisks = currentState.riskItems?.byAssessment?.[this.assessmentUuid]?.navigatorRisks || {};
      const areaRisks = navigatorRisks[areaKey]?.areaRisks || {};

      console.log(`[NavigatorEvaluationService] State source for area: ${areaKey}`);
      console.log(`[NavigatorEvaluationService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[NavigatorEvaluationService] Available risk categories:`, Object.keys(areaRisks));

      // Find risks that need evaluation
      const risksToEvaluate = [];

      Object.entries(areaRisks).forEach(([category, riskArray]) => {
        console.log(`[NavigatorEvaluationService] Checking category "${category}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[NavigatorEvaluationService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && !risk.priority) {
              console.log(`[NavigatorEvaluationService] FOUND unevaluated risk: "${risk.riskName}"`);
              risksToEvaluate.push({
                ...risk,
                areaKey,
                category
              });
            }
          });
        }
      });

      if (risksToEvaluate.length > 0) {
        console.log(`[NavigatorEvaluationService] Evaluating ${risksToEvaluate.length} risks using Navigator FastTrack approach`);

        let evaluatedCount = 0;
        for (const risk of risksToEvaluate) {
          try {
            const sourceCauses = this.extractCausesForRisk(risk, areaDefinition);

            console.log(`[NavigatorEvaluationService] Causes for ${risk.riskName}:`, {
              causesCount: sourceCauses?.length || 0
            });

            // Call Navigator-specific FastTrack evaluation
            const evalResponse = await authFetch('/.netlify/functions/navigatorPrioritizeRisks', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                riskName: risk.riskName,
                areaName: areaDefinition?.label || areaKey,
                evaluationData: {
                  causes: sourceCauses,
                  category: risk.category,
                  rationale: risk.rationale,
                  confidence: risk.confidence
                },
                assessmentUuid: this.assessmentUuid,
                parentAssessmentUuid: this.contextData?.parentAssessmentUuid,
                businessContext: this.contextData?.businessContext,
                stakeholders: this.contextData?.stakeholders || []
              })
            });

            if (evalResponse.ok) {
              const evalData = await evalResponse.json();
              if (evalData.fastTrackEvaluation) {
                const evaluation = evalData.fastTrackEvaluation;
                const priority = evaluation.priority?.toLowerCase() || 'moderate';
                const rationale = evaluation.rationale || '';

                console.log(`[NavigatorEvaluationService] FastTrack evaluated ${risk.riskName}: ${priority}`);

                await this.saveNavigatorEvaluation(risk, evaluation, priority, rationale, areaKey);

                evaluatedCount++;

                if (this.liveUpdateCallback) {
                  this.liveUpdateCallback('riskEvaluation', {
                    riskName: risk.riskName,
                    priority: priority,
                    rationale: rationale,
                    confidence: evaluation.confidence,
                    themes: evaluation.fastTrackData?.selectedThemes?.length || 0,
                    evaluatedCount,
                    totalRisks: risksToEvaluate.length,
                    areaKey
                  });
                }
              }
            } else {
              console.error(`[NavigatorEvaluationService] FastTrack evaluation failed for ${risk.riskName}:`, await evalResponse.text());
            }
          } catch (evalError) {
            console.error(`[NavigatorEvaluationService] Risk evaluation failed for ${risk.riskName}:`, evalError);
          }
        }
      }

      return {
        evaluatedRisks: risksToEvaluate.length,
        areaKey
      };

    } catch (error) {
      console.error('[NavigatorEvaluationService] Evaluation failed:', error);
      throw error;
    }
  }

  extractCausesForRisk(risk, areaDefinition) {
    try {
      const fromRiskSources = areaDefinition?.riskSources?.find(src => src.name === risk.category)?.causes;
      if (fromRiskSources && Object.keys(fromRiskSources).length > 0) {
        console.log(`[NavigatorEvaluationService] Found causes from riskSources for: ${risk.category}`);
        return Object.entries(fromRiskSources).map(([name, data]) => ({
          name,
          description: data.description || data.rationale || '',
          ...data
        }));
      }

      console.log(`[NavigatorEvaluationService] No causes found for risk category: ${risk.category}`);
      return [];
    } catch (error) {
      console.error('[NavigatorEvaluationService] Error extracting causes:', error);
      return [];
    }
  }

  async saveNavigatorEvaluation(risk, evaluation, priority, rationale, areaKey) {
    try {
      // STEP 1: Save evaluations to DB using RTK thunk (reuse Explorer evaluation infrastructure)
      const themes = evaluation.fastTrackData?.selectedThemes || [];

      if (themes.length > 0) {
        const savePromises = themes.map(theme =>
          this.dispatch(saveExplorerEvaluation({
            modelUuid: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskName: risk.riskName,
            riskId: risk.riskId,
            themeId: theme.themeId,
            riskArea: areaKey,
            priority: priority,
            rationale: rationale,
            data: theme.evaluation?.impact || theme.highLevelAssessment || {}
          })).unwrap().catch(err => {
            console.error(`[NavigatorEvaluationService] Failed to save theme ${theme.themeId} for ${risk.riskName}:`, err);
            return { error: true };
          })
        );

        const results = await Promise.all(savePromises);
        const failedSaves = results.filter(r => r.error);

        if (failedSaves.length > 0) {
          console.error(`[NavigatorEvaluationService] Failed to save ${failedSaves.length}/${themes.length} theme evaluations for ${risk.riskName}`);
        } else {
          console.log(`[NavigatorEvaluationService] Successfully saved ${themes.length} theme evaluations for ${risk.riskName}`);
        }
      } else {
        console.log(`[NavigatorEvaluationService] No themes for ${risk.riskName}, saving with default theme`);
        try {
          await this.dispatch(saveExplorerEvaluation({
            modelUuid: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskName: risk.riskName,
            riskId: risk.riskId,
            themeId: 1,
            riskArea: areaKey,
            priority: priority,
            rationale: rationale,
            data: {}
          })).unwrap();
          console.log(`[NavigatorEvaluationService] Default evaluation saved for ${risk.riskName}`);
        } catch (saveError) {
          console.error(`[NavigatorEvaluationService] Failed to save default evaluation for ${risk.riskName}:`, saveError);
        }
      }

      // STEP 2: Update Navigator Redux state with priority
      this.dispatch(
        updateNavigatorRiskPriority({
          assessmentUuid: this.assessmentUuid,
          areaKey,
          category: risk.category,
          riskId: risk.riskId,
          priority,
          rationale
        })
      );

      // STEP 3: Store evaluation in evaluations slice (reuse Explorer evaluation slice)
      this.dispatch(
        setExplorerRiskEvaluation({
          assessmentUuid: this.assessmentUuid,
          areaKey,
          category: risk.category,
          riskId: risk.riskId,
          evaluation: {
            priority,
            rationale,
            confidence: evaluation.confidence,
            fastTrackData: evaluation.fastTrackData,
            evaluatedAt: new Date().toISOString()
          }
        })
      );

      console.log(`[NavigatorEvaluationService] Priority saved for ${risk.riskName}: ${priority}`);

    } catch (error) {
      console.error(`[NavigatorEvaluationService] Error saving Navigator evaluation for ${risk.riskName}:`, error);
    }
  }
}
